import { AppBreadcrumbs } from "@/components/layouts/app-breadcrumbs";
import PageLayout from "@/components/layouts/page.layout";
import { PAGE_META } from "@/config/page-meta";
import { RoutePaths } from "@/config/route-paths";
import { useAuth } from "@/contexts/auth";
import { EmptyCustomersList } from "@/features/customers/pages/customer-list/components/empty-customers-list";
import { ErrorCustomersList } from "@/features/customers/pages/customer-list/components/error-customers-list";
import { LoadingCustomersList } from "@/features/customers/pages/customer-list/components/loading-customers-list";
import { getCustomerListBreadcrumbs } from "@/features/customers/pages/customer-list/lib/get-customer-list-breadcrumbs";
import { usePageMeta } from "@/hooks/use-page-meta";
import {
  EntityList,
  ListSection,
  ListSectionFilters,
  PageHeader,
  SearchInput,
  SectionWithHeading,
} from "@cmde/shadcn";
import { useListSearch } from "@cmde/hooks";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import type { Customer } from "@/api/token/customer";
import CustomerItem from "./components/customer-item";

const CustomerSelectionPage = () => {
  const { t } = useTranslation();
  const [isSelectingCustomer, setIsSelectingCustomer] = useState(false);
  const { selectCustomer, customers, isLoading, customerId } = useAuth();
  const navigate = useNavigate();

  const onCustomerSelected = (customer: Customer) => {
    navigate(RoutePaths.organizations.list({ customerId: customer.customerId }));
    setIsSelectingCustomer(false);
  };

  const handleCustomerSelect = (selectedCustomerId: number) => {
    if (selectedCustomerId === customerId) {
      navigate(RoutePaths.organizations.list({ customerId: selectedCustomerId }));
      return;
    }

    setIsSelectingCustomer(true);
    selectCustomer({
      customerId: selectedCustomerId,
      onCustomerSelected,
    });
  };

  const breadcrumbs = useMemo(() => getCustomerListBreadcrumbs(), []);

  const { filteredData, query, setQuery } = useListSearch({
    data: customers || [],
    keys: ["name", "customerId"],
  });

  return (
    <>
      {usePageMeta(PAGE_META.customers)}
      <PageLayout>
        <div className="h-full">
          <PageHeader
            title={t("customers.list.messages.title")}
            subtitle={t("customers.list.messages.subtitle")}
            breadcrumbs={<AppBreadcrumbs breadcrumbs={breadcrumbs} />}
          />

          <SectionWithHeading heading="">
            <ListSection
              header={
                <ListSectionFilters
                  searchComponent={
                    <SearchInput
                      value={query}
                      onChange={setQuery}
                      placeholder={t("customers.list.search.placeholder")}
                      disabled={isLoading || isSelectingCustomer}
                    />
                  }
                />
              }
            >
              <EntityList
                isLoading={isLoading || isSelectingCustomer}
                emptyContent={<EmptyCustomersList />}
                loadingContent={<LoadingCustomersList />}
                errorContent={<ErrorCustomersList />}
                data={filteredData}
                renderItem={(customer) => (
                  <CustomerItem key={customer.customerId} customer={customer} onSelect={handleCustomerSelect} />
                )}
              />
            </ListSection>
          </SectionWithHeading>
        </div>
      </PageLayout>
    </>
  );
};

export default CustomerSelectionPage;
